import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, fileUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Shield, Trash2, MapPin, MessageCircle, Users, ChevronLeft, Search, Pencil, Loader2, Upload, X, Wifi, Mountain, Fuel, Sparkles, Flag, Ban, Clock, Radio, Store, FileImage, Eye, Globe } from 'lucide-react';
import { toast } from 'sonner';
import UserLink from '@/components/UserLink';
import ImageLightbox from '@/components/ImageLightbox';

const CATEGORY_META = {
  car_meet:    { label: 'Car Meet', color: '#00F0FF', Icon: Users },
  parking:     { label: 'Parking', color: '#FFD479', Icon: MapPin },
  gas_station: { label: 'Gas Station', color: '#10B981', Icon: Fuel },
  viewpoint:   { label: 'Viewpoint', color: '#9DEDFF', Icon: Mountain },
  s365:        { label: '365', color: '#FF7A00', Icon: Store },
  spot:        { label: 'Spot', color: '#A78BFA', Icon: Sparkles },
};

function EditSpotDialog({ spot, onClose, onSaved }) {
  const { t } = useI18n();
  const open = !!spot;
  const [form, setForm] = useState({ title: '', description: '', category: 'spot', image_path: null });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (spot) setForm({
      title: spot.title,
      description: spot.description || '',
      category: spot.category,
      image_path: spot.image_path || null,
    });
  }, [spot]);

  const handleFile = async (f) => {
    if (!f || !f.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((s) => ({ ...s, image_path: data.path }));
    } catch (e) { toast.error(e.response?.data?.detail || t('common.error')); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const save = async () => {
    setLoading(true);
    try {
      const { data } = await api.patch(`/spots/${spot.id}`, form);
      onSaved?.(data);
      toast.success(t('admin.edit_spot.saved'));
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || t('common.error')); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="glass-strong border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tighter">{t('admin.edit_spot.title')}</DialogTitle>
        </DialogHeader>
        {spot && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(CATEGORY_META).map(([key, meta]) => {
                const Icon = meta.Icon;
                const active = form.category === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`admin-edit-cat-${key}`}
                    onClick={() => setForm({ ...form, category: key })}
                    className={`p-3 rounded-xl border-2 transition-all text-left bg-white/5 ${active ? '' : 'border-white/10 hover:border-white/30'}`}
                    style={active ? { borderColor: meta.color, background: `${meta.color}11` } : {}}
                  >
                    <Icon className="w-4 h-4 mb-1" style={{ color: meta.color }} />
                    <div className="text-[11px] font-bold uppercase">{meta.label}</div>
                  </button>
                );
              })}
            </div>
            <div>
              <Label className="text-white/80">{t('addspot.field.title')}</Label>
              <Input
                data-testid="admin-edit-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/80">{t('addspot.field.desc')}</Label>
              <Textarea
                data-testid="admin-edit-desc"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-black/40 border-white/10 text-white resize-none"
              />
            </div>
            <div>
              <Label className="text-white/80">{t('admin.edit_spot.image')}</Label>
              {form.image_path ? (
                <div className="relative mt-1 rounded-xl overflow-hidden border border-white/10">
                  <img src={fileUrl(form.image_path)} alt="" className="w-full max-h-56 object-cover" data-testid="admin-edit-image-preview" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image_path: null })}
                    data-testid="admin-edit-image-remove"
                    className="absolute top-2 right-2 bg-black/70 rounded-full p-1.5 hover:bg-black"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="admin-edit-image-upload"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="mt-1 w-full border-2 border-dashed border-white/15 rounded-xl p-5 hover:border-[#00F0FF] hover:bg-white/5 transition-all text-center"
                >
                  {uploading ? (
                    <Loader2 className="w-5 h-5 mx-auto text-white/60 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mx-auto text-white/60" />
                      <div className="text-sm text-white/60 mt-2">{t('admin.edit_spot.upload_cta')}</div>
                    </>
                  )}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
            </div>
            <Button
              data-testid="admin-edit-save"
              onClick={save}
              disabled={loading || !form.title}
              className="w-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide rounded-full"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.edit_spot.save')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: `${color}22`, color }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-white/50">{label}</div>
          <div className="font-display font-black text-2xl tracking-tighter">{value ?? '—'}</div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [spots, setSpots] = useState([]);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingSpot, setEditingSpot] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [online, setOnline] = useState([]);
  const [reports, setReports] = useState([]);
  const [sanctions, setSanctions] = useState({ banned: [], blocked: [] });
  const [adminLiveMeets, setAdminLiveMeets] = useState([]);
  const [spotsSort, setSpotsSort] = useState('recent'); // 'recent' | 'title' | 'category' | 'likes'

  useEffect(() => {
    if (user === null) return;
    if (!user || user === false || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, sp, cm, us, on, rp, sa, lm] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/spots'),
        api.get('/admin/comments'),
        api.get('/admin/users'),
        api.get('/presence/online').catch(() => ({ data: { online: [] } })),
        api.get('/admin/reports').catch(() => ({ data: [] })),
        api.get('/admin/sanctions').catch(() => ({ data: { banned: [], blocked: [] } })),
        api.get('/live-meets').catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setSpots(sp.data);
      setComments(cm.data);
      setUsers(us.data);
      setOnline(on.data.online || []);
      setReports(rp.data || []);
      setSanctions(sa.data || { banned: [], blocked: [] });
      setAdminLiveMeets(lm.data || []);
    } catch (e) {
      toast.error('Erreur de chargement admin');
    } finally {
      setLoading(false);
    }
  };

  // Poll online users every 20s
  useEffect(() => {
    if (!user || user.role !== 'admin') return undefined;
    const i = setInterval(() => {
      api.get('/presence/online').then(({ data }) => setOnline(data.online || [])).catch(() => {});
    }, 20000);
    return () => clearInterval(i);
  }, [user]);

  const delSpot = async (s) => {
    if (!window.confirm(`Supprimer le spot "${s.title}" ?`)) return;
    try {
      await api.delete(`/spots/${s.id}`);
      setSpots((prev) => prev.filter((x) => x.id !== s.id));
      toast.success('Spot supprimé');
    } catch { toast.error('Erreur'); }
  };

  const delComment = async (c) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    try {
      await api.delete(`/spots/${c.spot_id}/comments/${c.id}`);
      setComments((prev) => prev.filter((x) => x.id !== c.id && x.parent_id !== c.id));
      toast.success('Commentaire supprimé');
    } catch { toast.error('Erreur'); }
  };

  const delUser = async (u) => {
    if (!window.confirm(`SUPPRIMER DÉFINITIVEMENT le compte de "${u.name || u.email}" ? Cette action est irréversible (spots, commentaires, convois, DMs et abonnés seront supprimés).`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      // Refresh stats lazily
      api.get('/admin/stats').then(({ data }) => setStats(data)).catch(() => {});
      toast.success(`Compte ${u.name || u.email} supprimé`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const reloadSanctions = () => api.get('/admin/sanctions').then(({ data }) => setSanctions(data)).catch(() => {});
  const banUser = async (u) => {
    if (!window.confirm(`Bannir définitivement ${u.name} ? Ils ne pourront plus se connecter.`)) return;
    try { await api.post(`/admin/users/${u.id}/ban`); toast.success(`${u.name} banni`); reloadSanctions(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const unbanUser = async (u) => {
    try { await api.post(`/admin/users/${u.id}/unban`); toast.success(`${u.name} débanni`); reloadSanctions(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const blockUser = async (u) => {
    const hStr = window.prompt(`Bloquer ${u.name} pendant combien d'heures ? (1-8760)`, '24');
    if (!hStr) return;
    const hours = parseInt(hStr, 10);
    if (!hours || hours < 1) { toast.error('Durée invalide'); return; }
    try { await api.post(`/admin/users/${u.id}/block`, { hours }); toast.success(`${u.name} bloqué pour ${hours}h`); reloadSanctions(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const unblockUser = async (u) => {
    try { await api.post(`/admin/users/${u.id}/unblock`); toast.success(`${u.name} débloqué`); reloadSanctions(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const resolveReport = async (r) => {
    try { await api.post(`/admin/reports/${r.id}/resolve`); setReports((prev) => prev.map((x) => x.id === r.id ? { ...x, status: 'resolved' } : x)); }
    catch (e) { toast.error('Erreur'); }
  };
  const delReport = async (r) => {
    try { await api.delete(`/admin/reports/${r.id}`); setReports((prev) => prev.filter((x) => x.id !== r.id)); }
    catch (e) { toast.error('Erreur'); }
  };
  const delLiveMeet = async (m) => {
    if (!window.confirm(`Supprimer le live meet "${m.title}" de ${m.user_name} ?`)) return;
    try { await api.delete(`/live-meets/${m.id}`); setAdminLiveMeets((prev) => prev.filter((x) => x.id !== m.id)); toast.success('Live meet supprimé'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const formatBlockedTime = (until) => {
    if (!until) return null;
    const ms = new Date(until).getTime() - Date.now();
    if (ms <= 0) return null;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h >= 24) return `${Math.floor(h / 24)}j ${h % 24}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };



  const sortedSpots = (() => {
    const arr = [...spots.filter((s) => !search ||
      `${s.title} ${s.description} ${s.author_name}`.toLowerCase().includes(search.toLowerCase()))];
    if (spotsSort === 'title') arr.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    else if (spotsSort === 'category') arr.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
    else if (spotsSort === 'likes') arr.sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
    else arr.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    return arr;
  })();
  const filteredSpots = sortedSpots;
  const filteredComments = comments.filter((c) => !search ||
    `${c.text} ${c.author_name}`.toLowerCase().includes(search.toLowerCase()));
  const filteredUsers = users.filter((u) => !search ||
    `${u.email} ${u.name}`.toLowerCase().includes(search.toLowerCase()));

  if (user === null) {
    return <div className="min-h-screen grid place-items-center text-white/60">Chargement...</div>;
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-6 bg-[#0A0A0A] text-white">
      <div className="max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-white">
          <ChevronLeft className="w-3 h-3" /> Accueil
        </Link>
        <div className="mt-3 flex items-center gap-3">
          <Shield className="w-7 h-7 text-[#FF007F]" />
          <h1 className="font-display text-3xl lg:text-5xl font-black uppercase tracking-tighter">
            Modération <span className="text-white/40">— Horizon.Meet&Cruise</span>
          </h1>
        </div>
        <p className="text-white/60 mt-1 text-sm">
          Connecté en tant que <span className="text-white font-semibold">{user?.name}</span> · {user?.email}
        </p>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-stats">
          <StatCard icon={Users} label="Utilisateurs" value={stats?.users} color="#00F0FF" />
          <StatCard icon={Eye} label="Visiteurs en ligne" value={stats?.visitors_online} color="#10B981" />
          <StatCard icon={Globe} label="Visiteurs (total)" value={stats?.visitors_all_time} color="#A78BFA" />
          <StatCard icon={MapPin} label="Spots" value={stats?.spots} color="#FF007F" />
          <StatCard icon={Users} label="Car Meets" value={stats?.car_meets} color="#00F0FF" />
          <StatCard icon={FileImage} label="Publications" value={stats?.posts} color="#FF7A00" />
          <StatCard icon={MessageCircle} label="Commentaires" value={stats?.comments} color="#FFD479" />
        </div>

        {/* Search */}
        <div className="mt-8 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-3 text-white/40" />
          <Input
            data-testid="admin-search"
            placeholder="Filtrer par titre, auteur, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-black/40 border-white/10 text-white placeholder:text-white/30 pl-9"
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="spots" className="mt-6">
          <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
            <TabsTrigger value="spots" data-testid="admin-tab-spots">Spots ({filteredSpots.length})</TabsTrigger>
            <TabsTrigger value="comments" data-testid="admin-tab-comments">Commentaires ({filteredComments.length})</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Utilisateurs ({filteredUsers.length})</TabsTrigger>
            <TabsTrigger value="reports" data-testid="admin-tab-reports">
              <span className="inline-flex items-center gap-1.5">
                <Flag className="w-3 h-3 text-red-300" /> Signalements ({reports.filter((r) => r.status === 'open').length})
              </span>
            </TabsTrigger>
            <TabsTrigger value="livemeets" data-testid="admin-tab-livemeets">
              <span className="inline-flex items-center gap-1.5">
                <span className="relative flex w-2 h-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00F0FF] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00F0FF]"></span>
                </span>
                Car Meets ({adminLiveMeets.length})
              </span>
            </TabsTrigger>
            <TabsTrigger value="sanctions" data-testid="admin-tab-sanctions">
              <span className="inline-flex items-center gap-1.5">
                <Ban className="w-3 h-3 text-red-400" /> Sanctions ({sanctions.banned.length + sanctions.blocked.length})
              </span>
            </TabsTrigger>
            <TabsTrigger value="online" data-testid="admin-tab-online">
              <span className="inline-flex items-center gap-1.5">
                <span className="relative flex w-2 h-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                </span>
                En ligne ({online.length})
              </span>
            </TabsTrigger>
          </TabsList>

          {/* SPOTS */}
          <TabsContent value="spots" className="mt-4">
            <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="admin-spots-sort">
              <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold mr-1">Trier :</span>
              {[
                { key: 'recent', label: 'Récents' },
                { key: 'title', label: 'Titre' },
                { key: 'category', label: 'Catégorie' },
                { key: 'likes', label: 'Likes' },
              ].map((s) => (
                <button
                  key={s.key}
                  data-testid={`admin-spots-sort-${s.key}`}
                  onClick={() => setSpotsSort(s.key)}
                  className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold transition ${
                    spotsSort === s.key
                      ? 'bg-[#FFD479] text-black'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {loading ? (
              <div className="text-white/50 py-10 text-center">Chargement...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredSpots.map((s) => (
                  <div key={s.id} data-testid={`admin-spot-${s.id}`} className="glass rounded-2xl overflow-hidden flex">
                    <button
                      type="button"
                      onClick={() => s.image_path && setLightbox(s.image_path)}
                      className="w-28 h-28 bg-black/40 flex-shrink-0 grid place-items-center hover:opacity-90 transition"
                      data-testid={`admin-spot-image-${s.id}`}
                    >
                      {s.image_path ? (
                        <img src={fileUrl(s.image_path)} alt={s.title} className="w-full h-full object-cover" />
                      ) : (
                        <MapPin className="w-6 h-6 text-white/30" />
                      )}
                    </button>
                    <div className="flex-1 p-3 flex flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-display font-bold text-sm uppercase tracking-tight truncate">{s.title}</div>
                          <div className="text-[11px] text-white/50">
                            par {s.author_name} · {new Date(s.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                        <span
                          className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border whitespace-nowrap"
                          style={{
                            borderColor: (CATEGORY_META[s.category] || CATEGORY_META.spot).color,
                            color: (CATEGORY_META[s.category] || CATEGORY_META.spot).color,
                          }}
                        >
                          {(CATEGORY_META[s.category] || CATEGORY_META.spot).label}
                        </span>
                      </div>
                      <div className="text-xs text-white/60 mt-1 line-clamp-2 flex-1">{s.description || '—'}</div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-[11px] text-white/40">
                          ♥ {s.likes_count} · 💬 {s.comments_count || 0}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            data-testid={`admin-edit-spot-${s.id}`}
                            onClick={() => setEditingSpot(s)}
                            size="sm"
                            variant="ghost"
                            className="text-[#00F0FF] hover:bg-[#00F0FF]/10 h-7 px-2"
                            title={t('admin.edit_spot')}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            data-testid={`admin-delete-spot-${s.id}`}
                            onClick={() => delSpot(s)}
                            size="sm"
                            variant="ghost"
                            className="text-red-300 hover:bg-red-500/10 h-7 px-2"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredSpots.length === 0 && (
                  <div className="text-white/40 text-sm py-10 text-center col-span-2">Aucun spot.</div>
                )}
              </div>
            )}
          </TabsContent>

          {/* COMMENTS */}
          <TabsContent value="comments" className="mt-4">
            <div className="space-y-2">
              {filteredComments.map((c) => (
                <div key={c.id} data-testid={`admin-comment-${c.id}`} className="glass rounded-xl p-3 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#00F0FF] text-black grid place-items-center text-xs font-bold flex-shrink-0">
                    {c.author_name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white/50">
                      <span className="font-semibold text-white/80">{c.author_name}</span>
                      {c.parent_id && <span className="ml-2 text-[#00F0FF]/70">↳ réponse</span>}
                      <span> · {new Date(c.created_at).toLocaleString('fr-FR')}</span>
                      <span className="ml-2">· ♥ {c.likes_count || 0}</span>
                    </div>
                    <div className="text-sm text-white/80 mt-1 whitespace-pre-wrap break-words">{c.text}</div>
                    <div className="text-[10px] text-white/30 mt-1 font-mono">spot: {c.spot_id}</div>
                  </div>
                  <Button
                    data-testid={`admin-delete-comment-${c.id}`}
                    onClick={() => delComment(c)}
                    size="sm"
                    variant="ghost"
                    className="text-red-300 hover:bg-red-500/10 h-8"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {filteredComments.length === 0 && (
                <div className="text-white/40 text-sm py-10 text-center">Aucun commentaire.</div>
              )}
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="mt-4">
            <div className="glass rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/50">
                  <tr>
                    <th className="text-left p-3">Pseudo</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Rôle</th>
                    <th className="text-left p-3">Inscription</th>
                    <th className="text-right p-3 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-3 font-semibold">
                        <UserLink userId={u.id} name={u.name || '—'} />
                      </td>
                      <td className="p-3 text-white/70">{u.email}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                          u.role === 'admin' ? 'bg-[#FF007F]/20 text-[#FF007F] border border-[#FF007F]' : 'bg-white/10 text-white/60'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="p-3 text-white/50 text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="p-3 text-right">
                        {u.role !== 'admin' && u.id !== user?.id && (
                          <div className="inline-flex gap-1 justify-end">
                            <Button
                              data-testid={`admin-block-user-${u.id}`}
                              onClick={() => blockUser(u)}
                              size="sm"
                              variant="ghost"
                              className="text-orange-300 hover:bg-orange-500/10 h-7 px-2"
                              title="Bloquer temporairement"
                            >
                              <Clock className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              data-testid={`admin-ban-user-${u.id}`}
                              onClick={() => banUser(u)}
                              size="sm"
                              variant="ghost"
                              className="text-red-300 hover:bg-red-500/10 h-7 px-2"
                              title="Bannir définitivement"
                            >
                              <Ban className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              data-testid={`admin-delete-user-${u.id}`}
                              onClick={() => delUser(u)}
                              size="sm"
                              variant="ghost"
                              className="text-red-300 hover:bg-red-500/10 h-7 px-2"
                              title="Supprimer le compte"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={5} className="text-white/40 py-10 text-center">Aucun utilisateur.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* REPORTS */}
          <TabsContent value="reports" className="mt-4">
            <div className="glass rounded-2xl overflow-hidden" data-testid="admin-reports-list">
              {reports.length === 0 ? (
                <div className="text-white/40 text-sm py-10 text-center">
                  <Flag className="w-7 h-7 mx-auto mb-2 text-white/20" /> Aucun signalement.
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {reports.map((r) => (
                    <div key={r.id} data-testid={`admin-report-${r.id}`} className="p-4 hover:bg-white/5 transition">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-xs">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                              r.status === 'open' ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'bg-white/10 text-white/50'
                            }`}>{r.status}</span>
                            <span className="text-white/50">par <UserLink userId={r.reporter_id} name={r.reporter_name} className="text-white/80 font-semibold" /></span>
                            <span className="text-white/40">→</span>
                            <UserLink userId={r.target_id} name={r.target_name} className="text-[#FF007F] font-semibold" />
                          </div>
                          <p className="text-sm text-white/80 mt-1 whitespace-pre-wrap break-words">{r.reason}</p>
                          {r.context && <div className="text-[10px] text-white/40 mt-1 font-mono">{r.context}</div>}
                          <div className="text-[10px] text-white/30 mt-1">{new Date(r.created_at).toLocaleString()}</div>
                        </div>
                        <div className="flex flex-col gap-1">
                          {r.status === 'open' && (
                            <Button data-testid={`admin-report-resolve-${r.id}`} onClick={() => resolveReport(r)} size="sm" variant="ghost" className="h-7 text-[10px] text-emerald-300 hover:bg-emerald-500/10">Résoudre</Button>
                          )}
                          <Button data-testid={`admin-report-delete-${r.id}`} onClick={() => delReport(r)} size="sm" variant="ghost" className="h-7 text-[10px] text-red-300 hover:bg-red-500/10"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* LIVE MEETS */}
          <TabsContent value="livemeets" className="mt-4">
            <div className="glass rounded-2xl overflow-hidden" data-testid="admin-livemeets-list">
              {adminLiveMeets.length === 0 ? (
                <div className="text-white/40 text-sm py-10 text-center">
                  <Radio className="w-7 h-7 mx-auto mb-2 text-white/20" /> Aucun car meet en direct.
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {adminLiveMeets.map((m) => (
                    <div key={m.id} data-testid={`admin-livemeet-${m.id}`} className="p-4 flex items-center gap-4 hover:bg-white/5 transition">
                      <div className="w-10 h-10 rounded-lg bg-[#00F0FF] text-black grid place-items-center font-bold">
                        <Radio className="w-4 h-4 animate-pulse" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-black uppercase tracking-tight">{m.title}</div>
                        <div className="text-xs text-white/60 mt-0.5 inline-flex items-center gap-2">
                          <UserLink userId={m.user_id} name={m.user_name} className="text-white/80 font-semibold" />
                          {m.convoy_name && (
                            <Link to={`/convoys/${m.convoy_id}`} className="text-[#00F0FF] hover:underline inline-flex items-center gap-1">
                              <Users className="w-3 h-3" /> {m.convoy_name} ({m.convoy_members_count}/{m.convoy_max_size})
                            </Link>
                          )}
                        </div>
                        <div className="text-[10px] text-white/40 mt-0.5 font-mono">
                          @ ({Math.round(m.x)}, {Math.round(m.y)}) · Démarré {m.started_at ? new Date(m.started_at).toLocaleString() : '—'}
                        </div>
                      </div>
                      <Button data-testid={`admin-livemeet-delete-${m.id}`} onClick={() => delLiveMeet(m)} size="sm" variant="ghost" className="text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* SANCTIONS */}
          <TabsContent value="sanctions" className="mt-4">
            <div className="space-y-6" data-testid="admin-sanctions">
              <div className="glass rounded-2xl overflow-hidden">
                <div className="px-4 py-3 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2">
                  <Ban className="w-4 h-4 text-red-400" />
                  <span className="font-display uppercase tracking-tighter font-black">Bannis ({sanctions.banned.length})</span>
                </div>
                {sanctions.banned.length === 0 ? (
                  <div className="text-white/40 text-sm py-8 text-center">Aucun compte banni.</div>
                ) : sanctions.banned.map((b) => (
                  <div key={b.id} data-testid={`admin-banned-${b.id}`} className="p-3 border-t border-white/5 flex items-center gap-3">
                    {b.avatar_url ? <img src={fileUrl(b.avatar_url)} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-red-500/20 grid place-items-center text-xs font-bold">{(b.name || '?')[0].toUpperCase()}</div>}
                    <div className="flex-1 min-w-0">
                      <UserLink userId={b.id} name={b.name} className="font-semibold" />
                      <div className="text-[11px] text-white/40">{b.email}</div>
                    </div>
                    <Button data-testid={`admin-unban-${b.id}`} onClick={() => unbanUser(b)} size="sm" variant="ghost" className="h-7 text-[10px] text-emerald-300 hover:bg-emerald-500/10">Débanir</Button>
                  </div>
                ))}
              </div>
              <div className="glass rounded-2xl overflow-hidden">
                <div className="px-4 py-3 bg-orange-500/10 border-b border-orange-500/20 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-300" />
                  <span className="font-display uppercase tracking-tighter font-black">Bloqués ({sanctions.blocked.length})</span>
                </div>
                {sanctions.blocked.length === 0 ? (
                  <div className="text-white/40 text-sm py-8 text-center">Aucun compte bloqué.</div>
                ) : sanctions.blocked.map((b) => (
                  <div key={b.id} data-testid={`admin-blocked-${b.id}`} className="p-3 border-t border-white/5 flex items-center gap-3">
                    {b.avatar_url ? <img src={fileUrl(b.avatar_url)} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-orange-500/20 grid place-items-center text-xs font-bold">{(b.name || '?')[0].toUpperCase()}</div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <UserLink userId={b.id} name={b.name} className="font-semibold" />
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-200 text-[10px] font-bold uppercase tracking-wider" data-testid={`admin-blocked-timer-${b.id}`}>
                          <Clock className="w-3 h-3" /> {formatBlockedTime(b.blocked_until) || 'expiré'}
                        </span>
                      </div>
                      <div className="text-[11px] text-white/40">{b.email}</div>
                    </div>
                    <Button data-testid={`admin-unblock-${b.id}`} onClick={() => unblockUser(b)} size="sm" variant="ghost" className="h-7 text-[10px] text-emerald-300 hover:bg-emerald-500/10">Débloquer</Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ONLINE */}

          <TabsContent value="online" className="mt-4">
            <div className="glass rounded-2xl overflow-hidden" data-testid="admin-online-list">
              {online.length === 0 ? (
                <div className="text-white/40 text-sm py-10 text-center">
                  <Wifi className="w-7 h-7 mx-auto mb-2 text-white/20" />
                  Aucun joueur en ligne.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/50">
                    <tr>
                      <th className="text-left p-3"></th>
                      <th className="text-left p-3">Pseudo</th>
                      <th className="text-left p-3">In-game</th>
                      <th className="text-left p-3">Vu il y a</th>
                    </tr>
                  </thead>
                  <tbody>
                    {online.map((o) => (
                      <tr key={o.user_id} data-testid={`admin-online-${o.user_id}`} className="border-t border-white/5 hover:bg-white/5">
                        <td className="p-3 w-12">
                          {o.avatar_url ? (
                            <img src={fileUrl(o.avatar_url)} alt={o.name} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center text-black font-bold text-xs">
                              {(o.name || '?')[0].toUpperCase()}
                            </div>
                          )}
                        </td>
                        <td className="p-3 font-semibold">
                          <UserLink userId={o.user_id} name={o.name || '—'} />
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-emerald-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
                          </span>
                        </td>
                        <td className="p-3 text-white/70 text-xs">{o.gamer_tag || '—'}</td>
                        <td className="p-3 text-white/50 text-xs">
                          {o.last_seen ? (() => {
                            const sec = Math.floor((Date.now() - new Date(o.last_seen).getTime()) / 1000);
                            if (sec < 60) return `${sec}s`;
                            const m = Math.floor(sec / 60);
                            return `${m}m`;
                          })() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <EditSpotDialog
        spot={editingSpot}
        onClose={() => setEditingSpot(null)}
        onSaved={(updated) => setSpots((prev) => prev.map((x) => x.id === updated.id ? updated : x))}
      />
      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
